import { useEffect, useRef } from 'react'
import { useReducedMotion } from '../../hooks/useReducedMotion'
import { useTheme } from '../../hooks/useTheme'

/**
 * The travel-globe rotation video, treated as a scrubbable animation
 * timeline rather than something that plays: the cursor's horizontal
 * position across this component picks the exact frame, from the start
 * orientation (Europe/Africa/Middle East) on the left to the end orientation
 * (India/Asia/Pacific) on the right. The video is never played — only ever
 * paused with `currentTime` set — same technique as the site's scroll-driven
 * scrub sections (JourneyShowcase, ClosingCta): blob source for reliable
 * seeking, an eased target→current loop that only runs while still
 * converging, and a seek is never queued while the decoder is already
 * mid-seek.
 *
 * Cursor X drives the timeline AND doubles as a secondary translate, so the
 * globe visibly drifts toward whichever side the cursor favours instead of
 * sitting dead still while only its rotation changes; cursor Y drives its
 * own much subtler translate + tilt. Neither secondary movement is large
 * enough to fight the timeline scrub for attention — it reads as "the globe
 * has a little physical give," not a second competing animation.
 *
 * Page SCROLL is a second, independent driver of the same timeline — as the
 * section travels up through the viewport the globe turns with it, the same
 * "scroll IS the timeline" idea as JourneyShowcase/ClosingCta, just applied
 * to a normal in-flow section instead of a pinned one. Scroll only writes
 * `target` while the pointer isn't actively over the globe, so a hover always
 * takes over for direct control and scrolling resumes the moment it leaves —
 * the two never fight over the same frame.
 *
 * The source artwork's own background is a flat cream fill, not transparent,
 * so it's baked to solid white — but a white rectangle would sit badly on
 * the dark theme's navy surface (the same mismatch fixed earlier for
 * JourneyShowcase/ClosingCta). There are therefore two encodes, keyed and
 * composited onto white / ink-950 respectively by scripts at build time, and
 * the component picks whichever matches the live theme.
 */
const GLOBE_VIDEO = { light: '/videos/globe-scrub.mp4', dark: '/videos/globe-scrub-dark.mp4' }
const GLOBE_POSTER = { light: '/videos/globe-scrub-poster.jpg', dark: '/videos/globe-scrub-dark-poster.jpg' }
const LERP = 0.12
const SETTLE_EPSILON = 0.004
const PARALLAX_X = 16 // px, secondary drift from cursor X — same X that drives the scrub also nudges position, so the globe visibly follows the cursor rather than just rotating in place
const PARALLAX_Y = 10 // px, secondary drift from cursor Y
const PARALLAX_ROTATE = 1.6 // deg, subtle secondary tilt from cursor Y
// Passive scroll only scrubs the first slice of the timeline, where most
// landmarks still sit front-on — a full 0..1 scroll range rotated the globe
// past its "all the landmarks" pose well before the section was done being
// looked at. Cursor hover still gets the entire 0..1 timeline to explore.
const SCROLL_RANGE = 0.18

function StaticGlobe({ className, theme }) {
  return (
    <div className={`relative aspect-[1280/720] w-full ${className}`}>
      <img
        src={GLOBE_POSTER[theme]}
        alt="A globe circled by famous world landmarks"
        className="absolute inset-0 h-full w-full object-contain"
      />
    </div>
  )
}

export function InteractiveGlobe({ className = '' }) {
  const reducedMotion = useReducedMotion()
  const { theme } = useTheme()
  const containerRef = useRef(null)
  const videoRef = useRef(null)

  useEffect(() => {
    if (reducedMotion) return
    const container = containerRef.current
    const video = videoRef.current
    if (!container || !video) return

    let objectUrl = null
    let cancelled = false
    let duration = 0
    let target = 0 // 0..1, normalized cursor X
    let targetY = 0 // -1..1, normalized cursor Y, for the secondary parallax only
    let current = 0
    let currentY = 0
    let raf = 0
    let hovering = false

    const clamp01 = (v) => Math.min(1, Math.max(0, v))

    // Scroll progress of the section through the viewport: 0 while it's
    // still below, 1 once it's fully scrolled past above — same formula as
    // useParallax's depth offset, reused here as the scroll-driven target.
    const scrollProgress = () => {
      const rect = container.getBoundingClientRect()
      const vh = window.innerHeight
      return clamp01((vh - rect.top) / (vh + rect.height))
    }

    const tick = () => {
      raf = 0
      current += (target - current) * LERP
      currentY += (targetY - currentY) * LERP
      // `current` (0..1, already driving the scrub) doubles as the X
      // parallax source, centred on the middle so the globe drifts toward
      // whichever side the cursor favours instead of only ever the right half.
      const px = (current - 0.5) * 2 * PARALLAX_X
      const py = currentY * PARALLAX_Y
      video.style.transform = `translate3d(${px.toFixed(2)}px, ${py.toFixed(2)}px, 0) rotate(${(currentY * PARALLAX_ROTATE).toFixed(2)}deg)`

      if (duration && !video.seeking) {
        const t = current * duration
        if (Math.abs(t - video.currentTime) > 1 / 60) video.currentTime = t
      }

      const settled = Math.abs(target - current) < SETTLE_EPSILON && Math.abs(targetY - currentY) < SETTLE_EPSILON
      if (!settled || video.seeking) raf = requestAnimationFrame(tick)
    }
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(tick)
    }

    const onMetadata = () => {
      duration = video.duration || 0
      // Start wherever the current scroll position already implies, so a
      // page that loads mid-scrolled past this section doesn't jump.
      if (!hovering) {
        target = scrollProgress() * SCROLL_RANGE
        current = target
      }
      schedule()
    }
    // Once a queued seek lands, re-check — current/target may have moved on
    // while the decoder was busy, so there could be another seek due.
    const onSeeked = schedule

    // Cursor position relative to THIS component's own box, not the window —
    // stays correct at any size, any position on the page.
    const onPointerMove = (event) => {
      hovering = true
      const rect = container.getBoundingClientRect()
      target = clamp01((event.clientX - rect.left) / rect.width)
      targetY = clamp01((event.clientY - rect.top) / rect.height) * 2 - 1
      schedule()
    }
    const onPointerLeave = () => {
      hovering = false
      // Hand control back to scroll from wherever the page is right now,
      // rather than leaving `target` pinned to the last mouse position —
      // otherwise scroll would have no visible effect until it caught up.
      target = scrollProgress() * SCROLL_RANGE
      schedule()
    }
    const onScroll = () => {
      if (hovering) return
      target = scrollProgress() * SCROLL_RANGE
      schedule()
    }

    const videoSrc = GLOBE_VIDEO[theme]

    video.addEventListener('loadedmetadata', onMetadata)
    video.addEventListener('seeked', onSeeked)
    // Direct URL first so frame 0 paints immediately; the blob fetch is
    // background insurance for hosts without byte-range support and only
    // takes over if the direct source never became ready (or errored).
    video.src = videoSrc
    fetch(videoSrc)
      .then((res) => {
        if (!res.ok || !(res.headers.get('content-type') || '').startsWith('video')) {
          throw new Error(`unexpected response for ${videoSrc}`)
        }
        return res.blob()
      })
      .then((blob) => {
        if (cancelled) return
        if (video.readyState === 0 || video.error) {
          objectUrl = URL.createObjectURL(blob)
          video.src = objectUrl
        }
      })
      .catch(() => {})

    // Pointermove is bound to the component's own box, so it only ever fires
    // while the cursor (or, on touch, an active drag) is actually over it.
    // Doubles as touch-drag support for free: touch only emits pointermove
    // while a finger is down, so this reads as hover on desktop and as
    // drag-to-scrub on touch with the same code.
    container.addEventListener('pointermove', onPointerMove, { passive: true })
    container.addEventListener('pointerleave', onPointerLeave, { passive: true })
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)

    return () => {
      cancelled = true
      if (raf) cancelAnimationFrame(raf)
      container.removeEventListener('pointermove', onPointerMove)
      container.removeEventListener('pointerleave', onPointerLeave)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      video.removeEventListener('loadedmetadata', onMetadata)
      video.removeEventListener('seeked', onSeeked)
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [reducedMotion, theme])

  if (reducedMotion) return <StaticGlobe className={className} theme={theme} />

  return (
    <div ref={containerRef} className={`relative aspect-[1280/720] w-full ${className}`}>
      {/* Never played — only ever paused with currentTime set by cursor X.
          No controls of any kind; this must read as an interactive globe,
          not a video player. */}
      <video
        ref={videoRef}
        poster={GLOBE_POSTER[theme]}
        muted
        playsInline
        preload="auto"
        disablePictureInPicture
        controls={false}
        tabIndex={-1}
        aria-label="Interactive globe — scroll, or move the cursor left and right over it, to turn the Earth from Europe toward Asia"
        className="absolute inset-0 h-full w-full object-contain will-change-transform"
      />
    </div>
  )
}
